import { Component, Renderer2, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantNewsarticleComponent } from '../relevant-newsarticle/relevant-newsarticle.component';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-about-kalpita',
  imports: [CommonModule, RelevantNewsarticleComponent],
  standalone: true,
  templateUrl: './about-kalpita.component.html',
  styleUrl: './about-kalpita.component.scss'
})
export class AboutKalpitaComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const aboutKalpitaNewsSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-press-release/about-kalpita",
          "url": "https://www.kalpitatechnologies.com/kalpita-press-release/about-kalpita",
          "name": "Driving India's Digital Future — Kalpita Technologies",
          "description": "SiliconIndia features Kalpita Technologies leading India's digital transformation with client-centric, customized technology solutions spanning software development, cloud, UI/UX, and analytics.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": [
            "https://www.siliconindia.com/news/general/driving-indias-digital-future-kalpita-technologies-leads-innovation-with-clientcentric-solutions-nid-230727-cid-1.html"
          ],
          "datePublished": "2025-03-01",
          "dateModified": "2025-03-01",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-press-release/about-kalpita#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "News", "item": "https://www.kalpitatechnologies.com/kalpita-press-release/inthenews" },
            { "@type": "ListItem", "position": 3, "name": "Driving India's Digital Future", "item": "https://www.kalpitatechnologies.com/kalpita-press-release/about-kalpita" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Driving India's Digital Future: Kalpita Technologies Leads Innovation with Client-Centric Solutions",
          "alternativeHeadline": "How Kalpita Technologies Delivers End-to-End Digital Transformation Through Software, Cloud, and Strategic Partnerships",
          "articleBody": "Kalpita Technologies is driving India's digital future by leading innovation with client-centric solutions... Featured in SiliconIndia, the company showcases its role as a key enabler of India's digital transformation.",
          "author": { "@type": "Organization", "name": "SiliconIndia" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-03-01"
        },
        {
          "@type": "HowTo",
          "name": "How to Drive Digital Transformation Like Kalpita Technologies",
          "description": "Step-by-step guide to building a client-centric technology strategy to drive digital success.",
          "step": [
            { "@type": "HowToStep", "name": "Portfolio Building", "text": "Develop comprehensive capabilities spanning software, cloud, analytics, and infrastructure." },
            { "@type": "HowToStep", "name": "Client-Centricity", "text": "Tailor every solution to specific business objectives and operational challenges." },
            { "@type": "HowToStep", "name": "Efficiency Gains", "text": "Leverage automation and data-driven tools to reduce overhead and accelerate processes." },
            { "@type": "HowToStep", "name": "Forge Partnerships", "text": "Collaborate with global technology providers for access to cutting-edge tools." },
            { "@type": "HowToStep", "name": "Invest in R&D", "text": "Foster an innovation-driven culture through continuous learning and technology research." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What role does Kalpita Technologies play in India's digital transformation?",
              "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies delivers customized software, cloud, and UI/UX solutions to help businesses compete in the global digital economy." }
            },
            {
              "@type": "Question",
              "name": "How does Kalpita invest in innovation?",
              "acceptedAnswer": { "@type": "Answer", "text": "By committing substantial resources to R&D and refining service offerings with the latest in enterprise technology trends." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(aboutKalpitaNewsSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  articles: ArticlePost[] = [
    {
      id: 1,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
    },
    {
      id: 2,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
    },
    {
      id: 3,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
    },
    {
      id: 4,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
    },
    {
      id: 5,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
    },
    {
      id: 6,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
    },
    {
      id: 7,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
    },
    {
      id: 8,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
    }
  ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
